import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import DataTable from '../UI/DataTable';
import DeleteConfirmatationModal from './DeleteConfirmatationModal';
import { useClientTable } from './useTableData';
import { apiGet, apiDelete } from './tableApi';
import Button from '../UI/Button';
import exportToExcel from '../../UtilityHookFunctions/exportToExcel';
import { formatExportDate } from '../../UtilityHookFunctions/fetchAllPages';

const DIAGNOSIS_KEYS = ['primary', 'secondary', 'third'];
const isDateKey = (key) => /date|createdAt|updatedAt/i.test(String(key));

/**
 * List screen for endpoints that return the whole collection in one response.
 * Searching and paging happen on the client; the request itself is unchanged
 * from before (the `Queries` object is passed straight through as params).
 */
export default function CustomTable({
  tablekeys,
  customHead,
  fetchUrl,
  Queries,
  DeleteUrl,
  View,
  OtherModificationButton = null,
  title = null,
  emptyMessage = 'No records found.',
}) {
  const [allRows, setAllRows] = useState(null);
  const { listType } = useParams();
  const navigate = useNavigate();

  const queriesKey = JSON.stringify(Queries || {});
  useEffect(() => {
    let cancelled = false;
    setAllRows(null);
    apiGet(fetchUrl, { params: Queries }).then((data) => {
      if (!cancelled) setAllRows(Array.isArray(data) ? data : []);
    });
    return () => {
      cancelled = true;
    };
  }, [listType, fetchUrl, queriesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const table = useClientTable(allRows, { searchKeys: tablekeys });

  const headers = (tablekeys || []).map((key, index) => ({
    key,
    label: customHead?.[index] ?? key,
    render: (value, row) => (DIAGNOSIS_KEYS.includes(key) ? row?.diagnosis?.[key] : value),
  }));

  // Exports every row matching the current search, across all pages.
  const handleExport = () => {
    const columns = (tablekeys || []).map((key, index) => ({
      key,
      label: customHead?.[index] ?? key,
      format: (value, row) => {
        if (DIAGNOSIS_KEYS.includes(key)) return row?.diagnosis?.[key] ?? '';
        if (isDateKey(key)) return formatExportDate(value);
        if (value && typeof value === 'object') return JSON.stringify(value);
        return value ?? '';
      },
    }));
    exportToExcel(table.filteredRows, title || listType || 'export', columns);
  };

  const hasActions = Boolean(View || DeleteUrl || OtherModificationButton);

  return (
    <div className="w-full p-4 md:p-6">
      <DataTable
        title={title}
        headers={headers}
        data={table.rows}
        total={table.total}
        isLoading={allRows === null}
        emptyMessage={emptyMessage}
        page={table.page}
        totalPages={table.totalPages}
        rowsPerPage={table.rowsPerPage}
        onPageChange={table.setPage}
        onRowsPerPageChange={table.setLimit}
        onSearch={table.setSearch}
        searchValue={table.query.search}
        toolbar={
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={allRows === null || !table.filteredRows?.length}
          >
            Export to Excel
          </Button>
        }
        actions={
          hasActions
            ? (item) => (
                <>
                  {View && (
                    <button
                      type="button"
                      onClick={() =>
                        View === 'My_Bid_View' ? navigate(`${item?.bidOn?.id}`) : navigate(`${item?._id}`)
                      }
                      className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                    >
                      {View === 'My_Bid_View' ? 'View' : View}
                    </button>
                  )}

                  {DeleteUrl && (
                    <DeleteConfirmatationModal
                      deleteFunction={() =>
                        apiDelete(typeof DeleteUrl === 'function' ? DeleteUrl(item) : DeleteUrl, 'Patient')
                      }
                      text={item?.name}
                      heading="Delete Item"
                    />
                  )}

                  {OtherModificationButton && <OtherModificationButton item={item} />}
                </>
              )
            : null
        }
      />
      <ToastContainer />
    </div>
  );
}
